import React from 'react';
import { motion } from 'framer-motion';
import { 
  Copy, 
  TrendingUp, 
  Users, 
  Star, 
  ArrowRight,
  Award,
  Target,
  Clock
} from 'lucide-react';

// Import professional copy trading image
const copyTradingImage = require('../assets/traders-profit-354772.jpg');

const CopyTradingSection = () => {
  const topTraders = [
    {
      name: 'Sarah M.',
      avatar: '👩‍💼',
      return: '+247%',
      followers: '12.8K',
      winRate: '89%',
      status: 'trending'
    },
    {
      name: 'Marcus C.',
      avatar: '👨‍💻',
      return: '+387%',
      followers: '8.9K',
      winRate: '92%',
      status: 'hot'
    },
    {
      name: 'David R.',
      avatar: '👨‍💼',
      return: '+156%',
      followers: '15.6K',
      winRate: '76%',
      status: 'stable'
    }
  ];

  const copyTradingFeatures = [
    {
      icon: <Target className="h-6 w-6 text-emerald-600" />,
      title: 'Expert Strategies',
      description: 'Copy proven strategies from professional traders'
    },
    {
      icon: <Clock className="h-6 w-6 text-blue-600" />,
      title: 'Automated Trading',
      description: 'Trades execute automatically 24/7, no manual work'
    },
    {
      icon: <Award className="h-6 w-6 text-purple-600" />,
      title: 'Verified Results',
      description: 'All trader performance is verified and transparent'
    }
  ];

  return (
    <section className="copy-trading-section">
      <div className="copy-trading-section-container">
        
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="copy-trading-section-header"
        >
          <div className="copy-trading-section-badge">
            <Copy className="copy-trading-section-badge-icon" />
            <span>Copy Trading</span>
          </div>
          <h2 className="copy-trading-section-title">
            Copy Successful Traders,
            <span className="copy-trading-section-title-accent"> Earn Automatically</span>
          </h2>
          <p className="copy-trading-section-subtitle">
            Mirror the trades of our top-performing traders and start earning from day one. 
            No experience required – just copy, relax, and profit.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="copy-trading-section-content"
          >
            
            {/* Top Traders Preview */}
            <div className="copy-trading-section-traders">
              <h3 className="copy-trading-section-traders-title">
                Top Performing Traders This Month
              </h3>
              
              <div className="copy-trading-section-traders-grid">
                {topTraders.map((trader, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                    className="copy-trading-section-trader-card"
                  >
                    <div className="copy-trading-section-trader-header">
                      <div className="copy-trading-section-trader-avatar">{trader.avatar}</div>
                      <div className="copy-trading-section-trader-info">
                        <div className="copy-trading-section-trader-name">{trader.name}</div>
                        <div className="copy-trading-section-trader-followers">
                          <Users className="h-3 w-3" />
                          {trader.followers}
                        </div>
                      </div>
                      <div className={`copy-trading-section-trader-status ${trader.status}`}>
                        {trader.status === 'trending' && '🔥'}
                        {trader.status === 'hot' && '⚡'}
                        {trader.status === 'stable' && '⭐'}
                      </div>
                    </div>
                    
                    <div className="copy-trading-section-trader-stats">
                      <div className="copy-trading-section-trader-stat">
                        <span className="copy-trading-section-stat-label">Return</span>
                        <span className="copy-trading-section-stat-value positive">{trader.return}</span>
                      </div>
                      <div className="copy-trading-section-trader-stat">
                        <span className="copy-trading-section-stat-label">Win Rate</span>
                        <span className="copy-trading-section-stat-value">{trader.winRate}</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Features */}
            <div className="copy-trading-section-features">
              {copyTradingFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                  className="copy-trading-section-feature"
                >
                  <div className="copy-trading-section-feature-icon">
                    {feature.icon}
                  </div>
                  <div className="copy-trading-section-feature-content">
                    <h4 className="copy-trading-section-feature-title">{feature.title}</h4>
                    <p className="copy-trading-section-feature-description">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="copy-trading-section-cta"
            >
              <motion.a
                href="https://dashboard.chartlinebrokerage.com"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="copy-trading-section-cta-primary"
              >
                Start Copy Trading
                <ArrowRight className="h-5 w-5 ml-2" />
              </motion.a>
              <motion.a
                href="/copy-trading"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="copy-trading-section-cta-secondary"
              >
                Learn More
              </motion.a>
            </motion.div>

            {/* Stats */}
            <div className="copy-trading-section-stats">
              <div className="copy-trading-section-stat-item">
                <div className="copy-trading-section-stat-number">247%</div>
                <div className="copy-trading-section-stat-label">Avg. Annual Return</div>
              </div>
              <div className="copy-trading-section-stat-item">
                <div className="copy-trading-section-stat-number">50K+</div>
                <div className="copy-trading-section-stat-label">Active Copy Traders</div>
              </div>
              <div className="copy-trading-section-stat-item">
                <div className="copy-trading-section-stat-number">$100</div>
                <div className="copy-trading-section-stat-label">Min. Investment</div>
              </div>
            </div>
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="copy-trading-section-image-container"
          >
            <div className="copy-trading-section-image-wrapper">
              <img
                src={copyTradingImage}
                alt="Copy Trading Success"
                className="copy-trading-section-image"
              />
              
              {/* Floating Success Card */}
              <div className="copy-trading-section-success-card">
                <div className="copy-trading-section-success-header">
                  <div className="copy-trading-section-success-indicator"></div>
                  <span>Portfolio Performance</span>
                </div>
                <div className="copy-trading-section-success-value">+18.3%</div>
                <div className="copy-trading-section-success-period">This Month</div>
                <div className="copy-trading-section-success-note">
                  <Star className="h-4 w-4 text-yellow-500" />
                  Copying 3 top traders
                </div>
              </div>

              {/* Live Trading Badge */}
              <div className="copy-trading-section-live-badge">
                <div className="copy-trading-section-live-dot"></div>
                <span>Live Trading Active</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CopyTradingSection; 